import {gql} from '@apollo/client';
import PropTypes from 'prop-types';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {ContainerSchema, StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaOwnerQuery} from 'schemas/SchemaOwnerUtil';
import {Optional} from 'util/Types';

import {ContainerSchemaSelectorOwnerQuery} from './__generated/ContainerSchemaSelectorOwnerQuery';
import {ContainerSchemaSelectorQuery} from './__generated/ContainerSchemaSelectorQuery';
import StorableSchemaSelector from './StorableSchemaSelector';
import {getValidContainerSchemasFromQuery} from './StorablesSchemaSelectorUtils';

export const CONTAINER_SCHEMA_SELECTOR_QUERY = gql`
  query ContainerSchemaSelectorQuery @skipAudit @engComponent(name: INVENTORY) {
    ...AllContainerSchemasGraphQL
  }
  ${getFragmentWrapper('AllContainerSchemas').fragment}
`;

export const CONTAINER_SCHEMA_SELECTOR_OWNER_QUERY = gql`
  query ContainerSchemaSelectorOwnerQuery @skipAudit @engComponent(name: INVENTORY) {
    containerSchemas {
      id
      registry {
        id
        organization {
          id
          name
        }
      }
    }
  }
`;

export interface ContainerSchemaSelectorProps {
  error?: string | null;
  isDisabled?: boolean;
  // Schema may be null if `shouldShowNoSchemaOption` is true.  Otherwise, it should not be null.
  onChange: (schema: ContainerSchema | null) => void;
  registryId?: string;
  selectedContainerSchemaId: Optional<string>;
  shouldShowNoSchemaOption: boolean;
  shouldDefaultIfSingleItem?: boolean;
}

function ContainerSchemaSelector(props: ContainerSchemaSelectorProps): JSX.Element | null {
  const result = useBenchlingQuery<ContainerSchemaSelectorQuery>(CONTAINER_SCHEMA_SELECTOR_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  if (result.type === QueryResultType.ERROR) {
    return null;
  }

  let containerSchemas: Array<StorableSchema> | null;
  if (result.type === QueryResultType.PENDING) {
    containerSchemas = null;
  } else {
    containerSchemas = getValidContainerSchemasFromQuery(result.data, props.registryId);
  }

  return (
    <SchemaOwnerQuery<ContainerSchemaSelectorOwnerQuery>
      query={CONTAINER_SCHEMA_SELECTOR_OWNER_QUERY}
      getIdOwnerTuples={(data) =>
        data.containerSchemas.map((schema) => ({
          id: schema.id,
          owner: schema.registry.organization.name,
        }))
      }
    >
      {({getSchemaOwner}) => (
        <StorableSchemaSelector
          error={props.error}
          isDisabled={Boolean(props.isDisabled)}
          onChange={props.onChange}
          schemas={containerSchemas}
          selectedSchemaId={props.selectedContainerSchemaId}
          shouldShowNoSchemaOption={props.shouldShowNoSchemaOption}
          // Overridden by props from redux
          starredSchemaIds={[]}
          getSchemaOwner={getSchemaOwner}
          shouldDefaultIfSingleItem={props.shouldDefaultIfSingleItem}
        />
      )}
    </SchemaOwnerQuery>
  );
}

ContainerSchemaSelector.propTypes = {
  error: PropTypes.string,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired, // called with `(selectedContainerSchema)`
  registryId: PropTypes.string,
  selectedContainerSchemaId: PropTypes.string,
  shouldShowNoSchemaOption: PropTypes.bool.isRequired,
};

ContainerSchemaSelector.defaultProps = {
  shouldShowNoSchemaOption: true,
};

export default ContainerSchemaSelector;
